import React, { useCallback, useState } from 'react'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { DefaultDialogProps } from 'src/Libs/modals'
import { Formik } from 'formik'
import { TextField } from 'src/Libs/inputs/formik'
import { object, string } from 'yup'
import useModals from 'src/Hooks/useModals'
import { Box } from '@mui/material'

interface FormInputType {
  value: string
}

export interface SimpleTextDialogProps extends DefaultDialogProps {
  onConfirm: (value: string, close: () => void) => void
  title: string
  description?: string
  okLabel?: string
  confirmLabel?: string
  confirmTitle: string
  confirmText: string
  abortLabel?: string
  value?: string
  placeholder?: string
}

const DEFAULT_MAX_TEXT_LENGTH = 100

const SimpleTextDialog: React.VFC<SimpleTextDialogProps> = ({
  place,
  onClose: onDialogClose,
  onConfirm,
  value,
  placeholder,
  confirmText,
  confirmTitle,
  confirmLabel,
  okLabel,
  abortLabel,
  title,
}) => {
  const { t } = useCustomTranslation()
  const { openConfirmationModal } = useModals()
  const [processing, setProcessing] = useState<boolean>(false)
  const initialFormValues = { value: value || '' } as FormInputType

  const validationSchema = object().shape({
    value: string()
      .required(t('validations.form.common.is-required'))
      .max(DEFAULT_MAX_TEXT_LENGTH, t('validations.form.common.max-text-length', { value: DEFAULT_MAX_TEXT_LENGTH })),
  })

  const handleFinalConfirm = useCallback(
    (description: string, onClose: () => void): void => {
      setProcessing(true)
      onClose()
      onConfirm(description, onDialogClose)
    },
    [onConfirm, onDialogClose]
  )

  const handleConfirm = useCallback(
    (values: FormInputType) => {
      openConfirmationModal({
        title: confirmTitle,
        text: confirmText,
        onConfirm: onClose => handleFinalConfirm(values.value, onClose),
        buttonText: [abortLabel || t('common.cancel'), confirmLabel || t('common.confirm')],
      })
    },
    [abortLabel, confirmLabel, confirmText, confirmTitle, handleFinalConfirm, openConfirmationModal, t]
  )

  return (
    <Formik initialValues={initialFormValues} onSubmit={handleConfirm} validationSchema={validationSchema}>
      {({ isValid, dirty, handleSubmit }) => {
        return (
          <Dialog
            title={title}
            onClose={onDialogClose}
            place={place}
            maxWidth="sm"
            actions={{
              primary: {
                name: okLabel || t('common.ok'),
                disabled: !isValid && !dirty,
                onClick: handleSubmit,
                isLoading: processing,
              },
              secondary: [
                {
                  name: abortLabel || t('common.cancel'),
                },
              ],
            }}
          >
            <Box sx={{ py: 1 }}>
              <form onSubmit={handleSubmit}>
                <TextField name={'value'} label={placeholder || t('common.name')} max={DEFAULT_MAX_TEXT_LENGTH} />
              </form>
            </Box>
          </Dialog>
        )
      }}
    </Formik>
  )
}

export default SimpleTextDialog
